import { 
  Truck, 
  Ship, 
  Scale, 
  Printer, 
  BarChart3,
  ArrowRight,
  CheckCircle2
} from 'lucide-react';
import { useSiteContent } from '../hooks/useSiteContent';

interface ProductItem {
  id: string;
  icon: string;
  title: string;
  subtitle: string;
  description: string;
  features: string[];
  color: string;
  bgColor: string;
  iconColor: string;
}

interface ProductsContent {
  title: string;
  subtitle: string;
  description: string;
  products: ProductItem[];
}

const iconMap: Record<string, React.ComponentType<{ className?: string }>> = {
  Truck,
  Ship,
  Scale,
  Printer,
  BarChart3,
};

const defaultContent: ProductsContent = {
  title: '全方位船运物流管理',
  subtitle: '产品模块',
  description: '从派船调度到计量结算，从自助审核到数据决策，构建完整的船运发货数字化管理体系',
  products: [
    {
      id: 'dispatch',
      icon: 'Truck',
      title: '派单平台',
      subtitle: '智能派单调度',
      description: '基于订单需求的智能派单系统，实现船舶资源的最优配置，支持在线预约、自助派单、实时跟踪等功能。',
      features: ['在线预约派船', '智能调度算法', '船舶实时定位', '船主管理', '运费结算'],
      color: 'from-ocean-400 to-ocean-600',
      bgColor: 'bg-ocean-50',
      iconColor: 'text-ocean-600',
    },
    {
      id: 'scheduling',
      icon: 'Ship',
      title: '船运调度系统',
      subtitle: '船舶调度管理',
      description: '全面的船舶调度管理平台，实现船舶进出港管理、泊位调度、电子报港等核心功能，提升港口作业效率。',
      features: ['船舶进出港管理', '泊位智能调度', '电子报港', '船期管理', '港口作业协同'],
      color: 'from-primary-400 to-primary-600',
      bgColor: 'bg-primary-50',
      iconColor: 'text-primary-600',
    },
    {
      id: 'weighing',
      icon: 'Scale',
      title: '船运计量系统',
      subtitle: '精准计量管理',
      description: '集成多种计量方式，实现装货前、装货中、装货后的全程计量管控。',
      features: ['料斗秤集成', '自助计量采集', '计量异常预警', '计量报表分析'],
      color: 'from-emerald-400 to-emerald-600',
      bgColor: 'bg-emerald-50',
      iconColor: 'text-emerald-600',
    },
    {
      id: 'printing',
      icon: 'Printer',
      title: '自助审核打印系统',
      subtitle: '无纸化办公',
      description: '支持提货单自助审核、电子签名、二维码打印等功能，实现发货流程的数字化和无纸化管理。',
      features: ['自助提货审核', '电子签名认证', '二维码提货单', '自动打印服务', '单据电子归档'],
      color: 'from-amber-400 to-amber-600',
      bgColor: 'bg-amber-50',
      iconColor: 'text-amber-600',
    },
    {
      id: 'dashboard',
      icon: 'BarChart3',
      title: '管理驾驶舱',
      subtitle: '数据可视化',
      description: '直观的数据可视化大屏，实时展示发货量、运输效率、库存状态等关键指标，辅助管理决策。',
      features: ['实时数据大屏', '多维度分析报表', '预警提醒推送', '移动端查看', '数据导出分享'],
      color: 'from-violet-400 to-violet-600',
      bgColor: 'bg-violet-50',
      iconColor: 'text-violet-600',
    },
  ],
};

export default function Products() {
  const { content } = useSiteContent('products');
  const data: ProductsContent = content?.content || defaultContent;

  const scrollToContact = () => {
    const element = document.querySelector('#contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const getIcon = (iconName: string) => {
    return iconMap[iconName] || Truck;
  };

  return (
    <section id="products" className="section-padding bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-ocean-50 rounded-full mb-4">
            <span className="w-2 h-2 bg-ocean-500 rounded-full" />
            <span className="text-ocean-600 text-sm font-medium">{data.subtitle}</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            {data.title}
          </h2>
          <p className="text-lg text-slate-600">
            {data.description}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {(data.products || defaultContent.products).map((product: ProductItem, index: number) => {
            const IconComponent = getIcon(product.icon);
            return (
              <div
                key={product.id}
                className={`group relative bg-white rounded-2xl p-8 border border-slate-100 hover:border-ocean-200 hover:shadow-xl hover:shadow-ocean-500/10 transition-all duration-300 ${
                  index === (data.products || defaultContent.products).length - 1 && (data.products || defaultContent.products).length % 2 === 1 ? 'lg:col-span-2 lg:max-w-2xl lg:mx-auto' : ''
                }`}
              >
                <div className="flex items-start gap-4 mb-6">
                  <div className={`w-14 h-14 ${product.bgColor} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <IconComponent className={`w-7 h-7 ${product.iconColor}`} />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-slate-900">{product.title}</h3>
                    <p className={`text-sm font-medium bg-gradient-to-r ${product.color} bg-clip-text text-transparent`}>
                      {product.subtitle}
                    </p>
                  </div>
                </div>

                <p className="text-slate-600 mb-6 leading-relaxed">
                  {product.description}
                </p>

                <div className="grid grid-cols-2 gap-3 mb-6">
                  {product.features.map((feature: string) => (
                    <div key={feature} className="flex items-center gap-2">
                      <CheckCircle2 className={`w-4 h-4 ${product.iconColor} flex-shrink-0`} />
                      <span className="text-sm text-slate-600">{feature}</span>
                    </div>
                  ))}
                </div>

                <button 
                  onClick={scrollToContact}
                  className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700 hover:text-ocean-600 transition-colors group-hover:gap-3"
                >
                  了解更多
                  <ArrowRight className="w-4 h-4" />
                </button>

                <div className={`absolute inset-0 bg-gradient-to-br ${product.color} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-300 pointer-events-none`} />
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
